import { useState } from "react";
import { Link } from "react-router-dom";
import { DataTable, EmptyState, ErrorState, LoadingSkeleton } from "@a2z/ui";
import type { DataTableColumn } from "@a2z/ui";
import { useTranslations } from "@/lib/i18n/intl";
import { keys } from "@/lib/i18n/portal";
import type { IntegrationLogListItem } from "@/lib/api/types";
import { useStoreOptionsQuery } from "@/modules/stores/services/queries/useStoreOptionsQuery";
import { useIntegrationLogsQuery } from "../services/queries/useIntegrationLogsQuery";
import { buildIntegrationLogQuery } from "../services/integration-log-search-params";
import {
  EMPTY_CELL,
  formatDurationMs,
  formatLogTimestamp,
} from "../services/integration-log-format";
import { IntegrationLogFilters } from "../components/IntegrationLogFilters";
import {
  IntegrationLogDirectionChip,
  IntegrationLogStatusChip,
} from "../components/IntegrationLogChips";
import { IntegrationLogReference } from "../components/IntegrationLogReference";
import { IntegrationLogDetailDrawer } from "../components/IntegrationLogDetailDrawer";
import type {
  IntegrationLogFilterValues,
  IntegrationLogsPageProps,
} from "../types/integration-logs.types";

export function IntegrationLogsPage({
  locale,
  limit,
  offset,
  search,
  storeId,
  status,
  direction,
  provider,
  dateFrom,
  dateTo,
}: IntegrationLogsPageProps) {
  const t = useTranslations();
  const [selectedLogId, setSelectedLogId] = useState<string | null>(null);

  const filters: IntegrationLogFilterValues = {
    search,
    storeId,
    status,
    direction,
    provider,
    dateFrom,
    dateTo,
  };

  const { data, isLoading, error, refetch } = useIntegrationLogsQuery({
    limit,
    offset,
    ...filters,
  });
  const { data: storesData } = useStoreOptionsQuery();

  const basePath = `/${locale}/admin/integration-logs`;

  const columns: DataTableColumn<IntegrationLogListItem>[] = [
    {
      id: "occurredAt",
      header: t(keys.integrationLogs.columnTime),
      width: "160px",
      cell: (log) => (
        <span className="block truncate text-[color:var(--text-secondary)]">
          {formatLogTimestamp(log.occurredAt, locale)}
        </span>
      ),
    },
    {
      id: "direction",
      header: t(keys.integrationLogs.columnDirection),
      width: "100px",
      cell: (log) => <IntegrationLogDirectionChip direction={log.direction} />,
    },
    {
      id: "provider",
      header: t(keys.integrationLogs.columnProvider),
      width: "120px",
      cell: (log) => (
        <span className="block truncate font-semibold text-[color:var(--text-primary)]">
          {log.provider}
        </span>
      ),
    },
    {
      id: "operation",
      header: t(keys.integrationLogs.columnOperation),
      cell: (log) => (
        <span className="block truncate text-[color:var(--text-secondary)]">
          {log.operation || EMPTY_CELL}
        </span>
      ),
    },
    {
      id: "store",
      header: t(keys.integrationLogs.columnStore),
      width: "150px",
      cell: (log) => (
        <span className="block truncate text-[color:var(--text-secondary)]">
          {log.storeName || EMPTY_CELL}
        </span>
      ),
    },
    {
      id: "reference",
      header: t(keys.integrationLogs.columnReference),
      width: "140px",
      cell: (log) => (
        <IntegrationLogReference
          locale={locale}
          reference={log.reference}
          entityType={log.entityType}
          entityId={log.entityId}
          storeId={log.storeId}
          stopRowPropagation
        />
      ),
    },
    {
      id: "status",
      header: t(keys.integrationLogs.columnStatus),
      width: "110px",
      cell: (log) => <IntegrationLogStatusChip status={log.status} />,
    },
    {
      id: "httpStatus",
      header: t(keys.integrationLogs.columnHttpStatus),
      width: "90px",
      align: "center",
      cell: (log) => (log.httpStatus === null ? EMPTY_CELL : log.httpStatus),
    },
    {
      id: "durationMs",
      header: t(keys.integrationLogs.columnDuration),
      width: "100px",
      align: "end",
      cell: (log) =>
        formatDurationMs(
          log.durationMs,
          t(keys.integrationLogs.durationSuffix),
        ),
    },
  ];

  if (isLoading) {
    return <LoadingSkeleton variant="list" rows={8} />;
  }

  if (error || !data) {
    return <ErrorState onRetry={refetch} />;
  }

  const hasPrev = offset > 0;
  const hasNext = offset + limit < data.total;
  const prevOffset = Math.max(offset - limit, 0);
  const nextOffset = offset + limit;

  return (
    <section className="flex w-full flex-col gap-5 lg:px-6 lg:py-6">
      <div className="flex flex-wrap items-end justify-between gap-4">
        <div className="min-w-0">
          <h1 className="text-[23px] font-bold leading-tight tracking-[-0.025em] lg:text-[28px] lg:font-semibold lg:tracking-[-0.01em] text-[color:var(--text-primary)]">
            {t(keys.integrationLogs.title)}
          </h1>
          <p className="mt-1 text-[13.5px] text-[color:var(--text-muted)]">
            {t(keys.integrationLogs.subtitle)}
          </p>
        </div>
        <IntegrationLogFilters
          basePath={basePath}
          limit={limit}
          locale={locale}
          initialFilters={filters}
          storeOptions={storesData?.items ?? []}
        />
      </div>

      <DataTable
        rows={data.items}
        columns={columns}
        rowKey={(log) => log.id}
        ariaLabel={t(keys.integrationLogs.title)}
        onRowClick={(log) => setSelectedLogId(log.id)}
        empty={
          <EmptyState
            title={t(keys.common.emptyTitle)}
            description={t(keys.integrationLogs.emptySubtitle)}
          />
        }
      />

      <div className="flex items-center justify-between text-sm">
        {hasPrev ? (
          <Link
            to={`${basePath}?${buildIntegrationLogQuery({
              limit,
              offset: prevOffset,
              filters,
            })}`}
            className="text-[color:var(--text-secondary)]"
          >
            {t(keys.common.previous)}
          </Link>
        ) : (
          <span className="text-[color:var(--text-disabled)]">
            {t(keys.common.previous)}
          </span>
        )}
        {hasNext ? (
          <Link
            to={`${basePath}?${buildIntegrationLogQuery({
              limit,
              offset: nextOffset,
              filters,
            })}`}
            className="text-[color:var(--text-secondary)]"
          >
            {t(keys.common.next)}
          </Link>
        ) : (
          <span className="text-[color:var(--text-disabled)]">
            {t(keys.common.next)}
          </span>
        )}
      </div>

      <IntegrationLogDetailDrawer
        locale={locale}
        logId={selectedLogId}
        onClose={() => setSelectedLogId(null)}
      />
    </section>
  );
}
